/**
 * THE CORNER DELIVERY, BEFORE IT IS STRUCK — the aim, and where that aim lands.
 *
 * Owner, 2026-08-09: *"in corner show a trail for the ball where its gonna land
 * like FIFA game"*.
 *
 * ── The bar this had to clear ───────────────────────────────────────────────
 *
 * This project has already DELETED an on-pitch marker for lying, and the note it
 * left in `client/src/render/indicators.ts` is the acceptance test for this one:
 *
 *   > **7 m was a CONSTANT**. It never read the charge, the loft or the power,
 *   > so it did not mark where the ball would land […] A real landing-spot
 *   > predictor would need the sim's own ballistics (§3.2 drag + Magnus […])
 *
 * So nothing here is tuned, and nothing here is a shape that merely looks
 * plausible. The chain is, end to end, the game's own code:
 *
 *   1. WHO he is aiming at — `choosePassTarget`, the same scorer `buildPass`
 *      strikes to and the same one the pass-target HUD ring already draws;
 *   2. HOW HARD — `chargeForPassDistance`, the exact rule `emitCross` uses to
 *      set `decCharge` for this delivery;
 *   3. WHAT LEAVES THE BOOT — `previewPassLaunch` (sim/ball.ts), which shares
 *      `isCrossPosition` / `passControlFootPoint` / `resolveAim` with the real
 *      strike by CALLING them, so the assist override that replaces the
 *      charge-derived speed is the real one;
 *   4. WHERE IT GOES — `predictFlight` (sim/ball-predict.ts), a line-for-line
 *      mirror of `integrateAero`: same drag curve, same Magnus clamp, same
 *      spin decay, same Euler at FIXED_DT.
 *
 * ── What it is honest about ────────────────────────────────────────────────
 *
 * It is THE AIM, and the caller is required to say so on screen. Two things stop
 * it being a guarantee, and both are reported rather than hidden:
 *
 *   - the taker is still TURNING. `p.yaw` swings toward `decAimYaw` at a finite
 *     turn rate, so until he is set the trail shows where he is pointing NOW.
 *     That is a live aim, which is exactly what a FIFA delivery arc is;
 *   - the AI re-decides at 10 Hz and `choosePassTarget` re-solves the lead
 *     against the receivers' live velocities at release.
 *
 * `exact` (from `previewPassLaunch`) additionally goes false if the sim is ever
 * put back to rolling real strike error — today `STRIKE_AIM_ERROR_ENABLED` is
 * false, so the launch is a pure function of the input and the arc is the truth.
 *
 * ── Cost and safety ────────────────────────────────────────────────────────
 *
 * Runs on the RENDER path, so: no RNG draws (`previewPassLaunch` documents why
 * omitting them is sound), no writes to WorldState, no allocation after the
 * caller's one-time `createPredictedFlight`. Strictly downstream (§15.2).
 */

import { MatchPhase, RestartStage, type WorldState } from '../sim/types.js';
import { previewPassLaunch, type PreviewLaunch } from '../sim/ball.js';
import {
  DEFAULT_PREDICT_AIR,
  predictFlight,
  type PredictedFlight,
} from '../sim/ball-predict.js';
import { choosePassTarget } from '../sim/pass-target.js';
import { chargeForPassDistance } from './individual.js';

/** Longest flight worth drawing. A corner is airborne ~1.5 s; 4 s is slack. */
const CORNER_TRAIL_HORIZON_S = 4;
/** Thin the polyline to ~20 Hz. The INTEGRATION stays at 60 Hz — see predictFlight. */
const CORNER_TRAIL_SAMPLE_TICKS = 3;

/** Local, because distXZ is file-private in both ball.ts and individual.ts. */
function distXZ(ax: number, az: number, bx: number, bz: number): number {
  const dx = bx - ax;
  const dz = bz - az;
  return Math.sqrt(dx * dx + dz * dz);
}

const launch: PreviewLaunch = {
  px: 0, py: 0, pz: 0, vx: 0, vy: 0, vz: 0, wx: 0, wy: 0, wz: 0, exact: true,
};

/** Everything a renderer needs to draw the arc and its landing ring. */
export interface CornerDelivery {
  /** False ⇒ draw nothing. Never draw a stale arc. */
  show: boolean;
  /** The taker, or -1. */
  taker: number;
  /** False when the sim would roll a strike error this cannot know. */
  exact: boolean;
  flight: PredictedFlight;
}

/**
 * Refresh `out` for the corner currently being staged, if there is one.
 *
 * Returns `out.show`. Call once per rendered frame; it is cheap and idempotent.
 */
export function previewCornerDelivery(state: WorldState, out: CornerDelivery): boolean {
  const m = state.match;
  out.show = false;
  out.taker = -1;

  // Only a corner, and only while it is still being STAGED. Once it goes Live
  // the ball is really flying and the real ball is the honest thing to look at.
  if (m.phase !== MatchPhase.Corner) return false;
  if (m.restartStage !== RestartStage.Setup && m.restartStage !== RestartStage.Ready) return false;

  const taker = m.restartTaker;
  if (taker < 0) return false;
  const p = state.players;
  if (p.active[taker] !== 1 || p.sentOff[taker] === 1) return false;

  // His live facing IS his aim: player-controller turns `yaw` toward the
  // `aimYaw` the AI decided, so reading yaw needs no access to the brain (which
  // the client does not have — the AI runs inside step()).
  const aimYaw = p.yaw[taker]!;

  // The delivery the AI will actually pick, by its own rule. `emitCross` sets
  // `decCharge = chargeForPassDistance(dist to target)`, and `choosePassTarget`
  // is the target selector the strike itself uses.
  const target = choosePassTarget(state, taker, aimYaw);
  const dist = target >= 0
    ? distXZ(p.px[taker]!, p.pz[taker]!, p.px[target]!, p.pz[target]!)
    : distXZ(p.px[taker]!, p.pz[taker]!, 0, 0);
  const charge = chargeForPassDistance(dist);

  // `lofted` = true: emitCross now emits KickIntent.ThroughBall, which is the
  // flag buildPass reads to take its lofted branch. (It used to emit a flat
  // Pass and rely on liftBias, which buildPass does not read at all — that bug
  // was found BY this predictor drawing a corner rolling along the goal line.)
  if (!previewPassLaunch(state, taker, aimYaw, charge, true, launch)) return false;

  predictFlight(launch, DEFAULT_PREDICT_AIR, CORNER_TRAIL_HORIZON_S, CORNER_TRAIL_SAMPLE_TICKS, out.flight);

  out.show = out.flight.count > 1;
  out.taker = taker;
  out.exact = launch.exact;
  return out.show;
}
